// Fabric mapping for the Material filter (#1809).
//
// The client approved 13 fibres and excluded the rest. Searchspring's list-filter
// Advanced panel offers only a Collapse checkbox, with no value renaming and no
// exclusions, so every raw `x-fabric` tag value is mapped here in the theme.
//
// Two passes. Phrases run first and their matched text is removed from the
// string, so a specific fibre cannot also match its generic parent: "46% organic
// cotton" yields Organic Cotton alone, never Cotton as well. Whatever survives is
// split on /[^a-z]+/ and matched whole-token. Splitting on non-letters is what
// makes percentages disappear by themselves, so there is no digit-stripping rule
// to get subtly wrong, and "14k gold" still resolves because its phrase runs
// before the split.
//
// Whole-token matching is load-bearing rather than stylistic: as substrings, "wo"
// would match every "wool" and "lin" every "linen".

const FABRIC_PHRASES: [string, string][] = [
  ["organic cotton", "Organic Cotton"],
  ["merino wool", "Merino Wool"],
  ["sterling silver", "Sterling Silver"],
  ["14k gold", "14k Gold"],
]

// Abbreviations and misspellings actually present in the live tag data: wo, lin,
// ly and aplaca. Everything else abbreviated in the catalogue (vis, poly, ten,
// nyl, liama and so on) sits on an excluded fibre and needs no entry here.
// "ly" is read as lyocell rather than lycra: the elastane in the one value it
// fires on is already spelled "elas", and 47% is a primary fibre share.
//
// Plain cotton is deliberately absent, so only Organic Cotton surfaces (client
// request, 4 Sep 2026). Its phrase above still matches first, so organic pieces
// resolve; everything else containing cotton, and the "ctn" abbreviation, now
// map to nothing. Restore `cotton` and `ctn` here to bring the option back.
const FABRIC_TOKENS: Record<string, string> = {
  wool: "Wool",
  wo: "Wool",
  linen: "Linen",
  lin: "Linen",
  lyocell: "Lyocell",
  ly: "Lyocell",
  alpaca: "Alpaca",
  aplaca: "Alpaca",
  silk: "Silk",
  cupro: "Cupro",
  leather: "Leather",
  bamboo: "Bamboo",
}

/**
 * Every approved fibre a raw `x-fabric` tag value should surface under. A blend
 * returns several labels, so "70% cotton 30% linen" is findable under both
 * Cotton and Linen. Returns an empty array for excluded fibres, so anything the
 * client did not approve drops out of the filter on its own.
 */
export const fabricLabels = (rawValue: string): string[] => {
  let remainder = rawValue.toLowerCase()
  const labels = new Set<string>()

  FABRIC_PHRASES.forEach(([phrase, label]) => {
    if (!remainder.includes(phrase)) return
    labels.add(label)
    remainder = remainder.split(phrase).join(" ")
  })

  remainder.split(/[^a-z]+/).forEach((token) => {
    const label = FABRIC_TOKENS[token]
    if (label) labels.add(label)
  })

  return Array.from(labels)
}
